import React, { useState, useCallback } from 'react';
import { useFetch } from '../hooks/useFetch';
import { userAPI } from '../api/client';
import { Card, Badge, Spinner, EmptyState, StatCard, fmt, fmtDate } from '../components/UI';

export default function Earnings() {
  const [page, setPage] = useState(1);
  const fetchEarnings = useCallback(() => userAPI.earnings(page), [page]);
  const { data, loading, error } = useFetch(fetchEarnings);

  /* Pull true cross-page totals from the dashboard endpoint */
  const fetchDashboard = useCallback(() => userAPI.dashboard(), []);
  const { data: dash } = useFetch(fetchDashboard);

  const earnings    = data?.earnings    || [];
  const pagination  = data?.pagination  || {};

  const totalEarnings    = dash?.stats?.total_earned     ?? 0;
  const availableBalance = dash?.stats?.available_balance ?? 0;
  const pendingEarnings  = dash?.stats?.pending_earnings ?? 0;

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.h1}>Earnings</h1>
          <p style={styles.sub}>All income generated across your connected platforms.</p>
        </div>
      </div>

      <div style={styles.statGrid} className="stat-grid-4">
        <StatCard label="Total Earnings"     value={fmt(totalEarnings)}    icon="💰" color="#4f6ef7" />
        <StatCard label="Available Balance"  value={fmt(availableBalance)} icon="🏦" color="#16a34a" />
        <StatCard label="Pending Earnings"   value={fmt(pendingEarnings)}  icon="⏳" color="#f4a61d" />
        <StatCard label="Total Records"      value={pagination.total || 0} icon="📊" color="#7c3aed" />
      </div>

      <Card>
        {loading ? <Spinner /> : error ? (
          <p style={{ color:'#dc2626', padding:16 }}>{error}</p>
        ) : earnings.length === 0 ? (
          <EmptyState icon="💰" message="No earnings records yet. They'll appear here as our team completes work on your platforms." />
        ) : (
          <>
            <div style={{ overflowX:'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['Platform','Total Earned','Current Earnings','Status','Notes','Date'].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {earnings.map(e => (
                    <tr key={e.id} style={styles.tr}>
                      <td style={styles.td}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ ...styles.platDot, background: pColor(e.platform_name) }} />
                          <span style={{ fontWeight:600, color:'#1a1f5e' }}>{e.platform_name}</span>
                        </div>
                      </td>
                      <td style={styles.td}><span style={{ color:'#636b89' }}>{fmt(e.amount_earned)}</span></td>
                      <td style={styles.td}><strong style={{ color:'#16a34a', fontFamily:"'Sora',sans-serif" }}>{fmt(e.user_share)}</strong></td>
                      <td style={styles.td}><Badge status={e.status} /></td>
                      <td style={styles.td}><span style={{ color:'#9099b4', fontSize:13 }}>{e.notes || '—'}</span></td>
                      <td style={styles.td}><span style={{ color:'#9099b4', fontSize:13 }}>{fmtDate(e.created_at)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {pagination.total > pagination.limit && (
              <div style={styles.pagination}>
                <button style={styles.pageBtn} disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                <span style={{ fontSize:13, color:'#636b89' }}>Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}</span>
                <button style={styles.pageBtn} disabled={page * pagination.limit >= pagination.total} onClick={() => setPage(p => p + 1)}>Next →</button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

const pColor = n => ({ 'Appen':'#ff6b35','Scale AI':'#4f6ef7','Remotasks':'#16a34a','Toloka':'#f4a61d','Prolific':'#7c3aed','Outlier AI':'#0891b2' }[n] || '#9099b4');

const styles = {
  wrap:       { padding:'32px', fontFamily:"'DM Sans',sans-serif" },
  header:     { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 },
  h1:         { fontFamily:"'Sora',sans-serif", fontSize:22, fontWeight:700, color:'#1a1f5e', margin:0 },
  sub:        { fontSize:14, color:'#9099b4', marginTop:4 },
  statGrid:   { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:24 },
  table:      { width:'100%', borderCollapse:'collapse', minWidth:600 },
  th:         { background:'#f8f9fc', padding:'11px 14px', textAlign:'left', fontSize:12, fontWeight:700, color:'#9099b4', textTransform:'uppercase', letterSpacing:'.4px', borderBottom:'1px solid #e2e6f0' },
  tr:         { borderBottom:'1px solid #f0f2f8', transition:'background .15s', cursor:'default' },
  td:         { padding:'13px 14px', fontSize:14, verticalAlign:'middle' },
  platDot:    { width:8, height:8, borderRadius:'50%', flexShrink:0 },
  pagination: { display:'flex', alignItems:'center', justifyContent:'center', gap:20, padding:'16px 0 4px' },
  pageBtn:    { padding:'7px 16px', border:'1.5px solid #e2e6f0', borderRadius:8, background:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', color:'#636b89' },
};
